import { appConfigStore } from 'Models/AppConfigStore';
import * as React from 'react';
import { getMessageByKey } from 'Stores/LanguageStore';
import alertToast from 'Util/ToastifyUtils';
import { isNotNullOrUndefined, isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { ToastOptions } from 'react-toastify';
import { PlayCheckInAudio } from 'Views/Components/_HumanWritten/AudioPlayer/AudioPlayer';
import { FormatPhone } from 'Util/StringUtils';
import { BookingEntity, CheckInStatusEntity } from 'Models/Entities';
import { EventDto } from './EventCheckInEntities/EventDto';
import {
	EventCheckInBookingOverviewDto,
	IEventCheckInBookingOverviewDto,
} from './EventCheckInEntities/EventCheckInOverviewDto';
import { getEventCheckInData } from 'Services/Api/_HumanWritten/CheckInService';
import { eventCheckInStore } from './EventContext/EventCheckInContext';

// ==================================================== Models ===================================================== //

export interface DisableContinueState {
	continue: boolean;
}

export enum EventCheckInSorter {
	Default = 'Default',
	Fullname = 'Fullname',
	FullnameDesc = 'FullnameDesc',
}

// ================================================== GQL Fetches ================================================== //

interface FetchDataResult {
	eventDetailDto: EventDto;
	bookingSummaries: EventCheckInBookingOverviewDto[];
	checkInStatuses: CheckInStatusEntity[];
}

/**
 * Returns event and booked bookings based on event id.
 */
export async function fetchEventCheckInData(eventId?: string): Promise<FetchDataResult | null> {
	if (!eventId) {
		return new Promise(resolve => resolve(null));
	}

	const response = await getEventCheckInData(eventId);

	// Process entities as a class to access class functions.
	const eventDetails = new EventDto(response?.data.eventDetails);
	const bookings = response?.data?.checkInBookingOverviews
		?.filter((x: any) => isNotNullOrUndefined(x))
		?.map((x: Partial<IEventCheckInBookingOverviewDto> | undefined) => new EventCheckInBookingOverviewDto(x));
	const filteredbookings = filterBookedBookings(bookings);
	const checkInStatuses = (response?.data?.checkInStatuses ?? []) as CheckInStatusEntity[];

	const statusList: CheckInStatusEntity[] = [];

	if (checkInStatuses.length > 0) {
		statusList.push(
			new CheckInStatusEntity({
				id: 'booked',
				order: 0,
				label: getMessageByKey('check-in-status__booked-status__label', 'Booked'),
				abbreviation: 'B',
				includedInCapacity: false,
				disembarked: false,
				confirmBooking: false,
				default: true,
				bookingType: 'EVENT_BOOKING',
				colour: appConfigStore.bookedStatusTagColour,
				actionString: '',
			}),
		);
	}

	checkInStatuses.forEach(x => {
		statusList.push(x);
	});

	// Sort statuses by order to ensure consistent display
	statusList.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

	return {
		eventDetailDto: eventDetails,
		bookingSummaries: filteredbookings,
		checkInStatuses: statusList ?? [],
	};
}

/**
 * Process has three steps in order:
 * - Filters bookings by BOOKED
 * - Instantiate class for each
 * - Order list by full name
 * @param bookings This list of bookings may not have instantiated classes.
 */
function filterBookedBookings(bookings: EventCheckInBookingOverviewDto[]): EventCheckInBookingOverviewDto[] {
	const bookedBookings = bookings;
	bookedBookings.sort(sortByFullName());

	return bookedBookings;
}
// =================================================== Reducers ==================================================== //

export function getTotalCustomTicketPassengers(bookings: EventCheckInBookingOverviewDto[]): number {
	return bookings.reduce((currentValue, booking) => {
		if (!booking.bookedSummary) {
			throw new Error('Booked summary missing');
		}

		if (isNullOrUndefined(booking.bookedSummary.selectedTickets)) {
			return currentValue;
		}

		let runningTotal = 0;
		for (const ticket of booking.bookedSummary.selectedTickets) {
			runningTotal += ticket.quantity;
		}

		return currentValue + runningTotal;
	}, 0);
}

export function checkedInCustomTicketPassengers(bookings: EventCheckInBookingOverviewDto[]): number {
	return bookings.reduce((currentValue, booking) => {
		if (!booking.checkedIn) {
			return currentValue;
		}

		if (!booking.bookedSummary) {
			throw new Error('Booked summary missing');
		}

		if (isNullOrUndefined(booking.bookedSummary.selectedTickets)) {
			return currentValue;
		}

		let runningTotal = 0;
		for (const ticket of booking.bookedSummary.selectedTickets) {
			runningTotal += ticket.quantity;
		}

		return currentValue + runningTotal;
	}, 0);
}

// ========================================== Table cell transformations =========================================== //

/**
 * If booking has a vehicle, driver name is used.
 *
 * Otherwise user account name is used.
 */
export function transformFullName(booking: BookingEntity | EventCheckInBookingOverviewDto) {
	const name = booking.getFullName(true);

	if (!name) {
		return null;
	}

	return <div className="fullname-cell">{name}</div>;
}

export function transformMobileNumber(booking: EventCheckInBookingOverviewDto) {
	if (!booking.bookedSummary) {
		throw new Error('Booked summary missing');
	}

	const { primaryPhone } = booking.bookedSummary;

	let phone = null;

	if (stringNotEmpty(primaryPhone)) {
		phone = primaryPhone;
	} else {
		phone = booking.user.phone;
	}

	if (phone === '') {
		return '-';
	}

	return FormatPhone(phone);
}

export function transformEmail(booking: EventCheckInBookingOverviewDto) {
	if (!booking.bookedSummary) {
		throw new Error('Booked summary missing');
	}

	const { primaryEmail } = booking.bookedSummary;

	let email = null;

	if (stringNotEmpty(primaryEmail)) {
		email = primaryEmail;
	} else {
		email = booking.user.email;
	}

	if (email === '') {
		return '-';
	}

	return email;
}

export function transformPassengerTicketHeader() {
	const values: string[] = [];
	const customTicketTypes = eventCheckInStore.eventDetails?.customTicketTypes ?? [];
	for (const ticket of customTicketTypes) {
		const value = ticket.singularName[0];
		values.push(value.toString());
	}
	// Return 'Tickets' as fallback if no custom ticket types defined
	return values.length > 0 ? values.join(' | ') : 'Tickets';
}

export function transformPassengerTickets(booking: BookingEntity | EventCheckInBookingOverviewDto) {
	const summary = booking.bookedSummary;
	if (!summary) {
		return null;
	}
	const values: string[] = [];
	const customTicketTypes = eventCheckInStore.eventDetails?.customTicketTypes ?? [];
	for (const ticket of customTicketTypes) {
		const value = summary.selectedTickets?.find(x => x.customTicketTypeId === ticket.id)?.quantity ?? 0;
		if (value) {
			values.push(value.toString());
		} else {
			// Use '-' as placeholder when value is undefined or 0
			values.push('-');
		}
	}
	return values.join(' | ');
}

/**
 * Returns first letter of booking's assigned name (vehicle booking uses driver last name,
 * passenger booking uses user's last name).
 */
export function firstLetterLastName(booking: BookingEntity | EventCheckInBookingOverviewDto) {
	const bookingName = booking.getFullName(true) ?? '';
	return bookingName.charAt(0);
}

// ================================================ Sort functions ================================================= //

export type BookingSorter = (a: EventCheckInBookingOverviewDto, b: EventCheckInBookingOverviewDto) => number;

export function sortByFullName(descending: boolean = false): BookingSorter {
	return (a: BookingEntity | EventCheckInBookingOverviewDto, b: BookingEntity | EventCheckInBookingOverviewDto) => {
		// Convert to upper case so that it sorts based on letters, not characters
		// e.g. Kate and karlo appears in the same sub-section, otherwise there will be two groups,
		// one for lowercase, another for uppercase
		const fullnameA = (a?.getFullName(true) ?? '').toUpperCase();
		const fullnameB = (b?.getFullName(true) ?? '').toUpperCase();
		if (fullnameA > fullnameB) return descending ? -1 : 1;
		if (fullnameA < fullnameB) return descending ? 1 : -1;
		return 0;
	};
}

// ================================================ Other functions ================================================ //

export const CHECK_IN_ALERT_CONFIG: ToastOptions = {
	autoClose: 2200,
	pauseOnFocusLoss: false,
	pauseOnHover: false,
	position: 'top-left',
};

/**
 * Display notification for two actions.
 *
 * If checkedIn is true, message says "John Smith checked in".
 *
 * Otherwise, message says "John Smith undo check-in".
 *
 * Notification will last 2 seconds and appears top-left of screen.
 */
export function checkInAlert(
	booking: BookingEntity | EventCheckInBookingOverviewDto,
	newStatus: string,
	nextStatus: boolean,
) {
	alertToast(booking.getFullName(true), nextStatus ? 'success' : undefined, newStatus, CHECK_IN_ALERT_CONFIG);
	if (nextStatus) {
		PlayCheckInAudio();
	}
}
